import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DownloadIcon from '@mui/icons-material/Download';
import GridViewIcon from '@mui/icons-material/GridView';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import SchoolIcon from '@mui/icons-material/School';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import FilterPanel from '../../lib/components/FilterPanel';
import StatCard from '../../lib/components/StatCard';
import TimetableGrid from '../../lib/components/TimetableGrid';
import { mockConflicts as rawMockConflicts } from '../Conflicts/MockData';
import { useFilterStore } from '../../stores/filterStore';
import { useTimetableStore } from '../../stores/timetableStore';
import type { Conflict, DayOfWeek, Lesson, ViewType } from '../../types';
import { colorSchemes, mockViewStats } from './MockData';

// Adapt mock conflicts from page MockData to the types/conflict.ts structure
const mockConflicts: Conflict[] = rawMockConflicts.map((c) => ({
  id: c.id,
  timetableId: 'timetable-current',
  type: c.type.replace(/_/g, '-') as Conflict['type'],
  severity: c.severity,
  affectedLessons: c.affectedLessons,
  message: c.description,
  status: 'unresolved' as const,
  detectedAt: new Date().toISOString(),
}));

const ViewTimetable: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);

  // Stores
  const { currentTimetable } = useTimetableStore();
  const { viewConfig, activeFilters, availableFilters, setViewType, updateFilter, clearFilters, getActiveFilterCount } =
    useFilterStore();

  // Compute filtered lessons
  const filteredLessons = useMemo(() => {
    if (!currentTimetable) return [];

    let lessons = [...currentTimetable.lessons];

    // Apply day filter
    if (activeFilters.day) {
      lessons = lessons.filter((l) => l.day === activeFilters.day);
    }

    // Apply period filter
    if (activeFilters.period) {
      lessons = lessons.filter((l) => l.period === activeFilters.period);
    }

    // Apply teacher filter
    if (activeFilters.teacherId) {
      lessons = lessons.filter((l) => l.teacherId === activeFilters.teacherId);
    }

    // Apply year group filter
    if (activeFilters.yearGroupId) {
      const yearGroupId = activeFilters.yearGroupId;
      lessons = lessons.filter((l) => l.studentGroupId.includes(yearGroupId));
    }

    // Apply room filter
    if (activeFilters.roomId) {
      lessons = lessons.filter((l) => l.roomId === activeFilters.roomId);
    }

    // Apply subject filter
    if (activeFilters.subjectId) {
      lessons = lessons.filter((l) => l.subjectId === activeFilters.subjectId);
    }

    return lessons;
  }, [currentTimetable, activeFilters]);

  // Get relevant conflicts for filtered lessons
  const relevantConflicts = useMemo(() => {
    const lessonIds = new Set(filteredLessons.map((l) => l.id));
    return mockConflicts.filter((c) => c.affectedLessons.some((id) => lessonIds.has(id)));
  }, [filteredLessons]);

  // Compute stats for the current view
  const viewStats = useMemo(() => {
    const uniqueTeachers = new Set(filteredLessons.map((l) => l.teacherId));
    const uniqueRooms = new Set(filteredLessons.map((l) => l.roomId));

    return {
      totalLessons: filteredLessons.length,
      teachersScheduled: uniqueTeachers.size,
      roomsUsed: uniqueRooms.size,
      conflictCount: relevantConflicts.length,
      coveragePercentage: mockViewStats.coveragePercentage,
      freePeriods: mockViewStats.freePeriods,
    };
  }, [filteredLessons, relevantConflicts]);

  // Get days to display based on view config
  const displayDays: DayOfWeek[] = useMemo(() => {
    if (activeFilters.day) {
      return [activeFilters.day];
    }
    return ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  }, [activeFilters.day]);

  // Handlers
  const handleViewTypeChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newViewType: ViewType | null) => {
      if (newViewType) {
        setViewType(newViewType);
        // Reset relevant filters when changing view type
        if (newViewType === 'whole-school') {
          clearFilters();
        }
      }
    },
    [setViewType, clearFilters],
  );

  const handleFilterChange = useCallback(
    (filters: {
      day?: DayOfWeek;
      period?: number;
      teacherId?: string;
      yearGroupId?: string;
      roomId?: string;
      subjectId?: string;
    }) => {
      Object.entries(filters).forEach(([key, value]) => {
        updateFilter(key as keyof typeof filters, value);
      });
    },
    [updateFilter],
  );

  const handleLessonClick = useCallback((lesson: Lesson) => {
    setSelectedLesson(lesson);
  }, []);

  const handleExport = useCallback(() => {
    // Export functionality placeholder
    console.log('Export timetable');
  }, []);

  const handleRefresh = useCallback(() => {
    // Refresh functionality placeholder
    console.log('Refresh timetable');
  }, []);

  // Filter panel content
  const filterPanelContent = (
    <FilterPanel
      availableFilters={{
        days: availableFilters.days,
        periods: availableFilters.periods,
        teachers: availableFilters.teachers,
        yearGroups: availableFilters.yearGroups,
        rooms: availableFilters.rooms,
        subjects: availableFilters.subjects,
      }}
      activeFilters={activeFilters}
      onFilterChange={handleFilterChange}
      onClearAll={clearFilters}
    />
  );

  // No timetable state
  if (!currentTimetable) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body1">
            No timetable available. Please generate a timetable first using the Generator.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2, gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            View Timetable
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {currentTimetable.name} - Version {currentTimetable.version}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {isMobile && (
            <Button variant="outlined" startIcon={<GridViewIcon />} onClick={() => setFilterDrawerOpen(true)}>
              Filters {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
            </Button>
          )}
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExport}>
            Export
          </Button>
        </Box>
      </Box>

      {/* View Type Selector */}
      <Card>
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" color="text.secondary">
              View Mode:
            </Typography>
            <ToggleButtonGroup
              value={viewConfig.viewType}
              exclusive
              onChange={handleViewTypeChange}
              size="small"
              sx={{ flexWrap: 'wrap' }}
            >
              <ToggleButton value="whole-school">
                <Tooltip title="Whole School">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SchoolIcon fontSize="small" />
                    {!isMobile && <span>Whole School</span>}
                  </Box>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="teacher">
                <Tooltip title="By Teacher">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PersonIcon fontSize="small" />
                    {!isMobile && <span>Teacher</span>}
                  </Box>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="year-group">
                <Tooltip title="By Year Group">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <GridViewIcon fontSize="small" />
                    {!isMobile && <span>Year Group</span>}
                  </Box>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="room">
                <Tooltip title="By Room">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarTodayIcon fontSize="small" />
                    {!isMobile && <span>Room</span>}
                  </Box>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Quick color scheme selector */}
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Color By</InputLabel>
                <Select
                  value={viewConfig.colorBy}
                  label="Color By"
                  onChange={(e: SelectChangeEvent) => {
                    // Color scheme change placeholder
                    console.log('Color scheme:', e.target.value);
                  }}
                >
                  {colorSchemes.map((scheme) => (
                    <MenuItem key={scheme.id} value={scheme.id}>
                      {scheme.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Stats Row */}
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard stat={{ label: 'Lessons', value: viewStats.totalLessons, color: 'info' }} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard stat={{ label: 'Teachers', value: viewStats.teachersScheduled, color: 'success' }} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard stat={{ label: 'Rooms', value: viewStats.roomsUsed, color: 'warning' }} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            stat={{
              label: 'Conflicts',
              value: viewStats.conflictCount,
              color: viewStats.conflictCount > 0 ? 'error' : 'success',
            }}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard stat={{ label: 'Coverage', value: `${viewStats.coveragePercentage}%`, color: 'info' }} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard stat={{ label: 'Free Periods', value: viewStats.freePeriods, color: 'warning' }} />
        </Grid>
      </Grid>

      {/* Active Filters Display */}
      {getActiveFilterCount() > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Active Filters:
          </Typography>
          {activeFilters.day && (
            <Chip
              label={`Day: ${activeFilters.day.charAt(0).toUpperCase() + activeFilters.day.slice(1)}`}
              size="small"
              onDelete={() => updateFilter('day', undefined)}
            />
          )}
          {activeFilters.teacherId && (
            <Chip
              label={`Teacher: ${availableFilters.teachers.find((t) => t.id === activeFilters.teacherId)?.name || activeFilters.teacherId}`}
              size="small"
              onDelete={() => updateFilter('teacherId', undefined)}
            />
          )}
          {activeFilters.yearGroupId && (
            <Chip
              label={`Year: ${availableFilters.yearGroups.find((y) => y.id === activeFilters.yearGroupId)?.name || activeFilters.yearGroupId}`}
              size="small"
              onDelete={() => updateFilter('yearGroupId', undefined)}
            />
          )}
          {activeFilters.roomId && (
            <Chip
              label={`Room: ${availableFilters.rooms.find((r) => r.id === activeFilters.roomId)?.name || activeFilters.roomId}`}
              size="small"
              onDelete={() => updateFilter('roomId', undefined)}
            />
          )}
          {activeFilters.subjectId && (
            <Chip
              label={`Subject: ${availableFilters.subjects.find((s) => s.id === activeFilters.subjectId)?.name || activeFilters.subjectId}`}
              size="small"
              onDelete={() => updateFilter('subjectId', undefined)}
            />
          )}
          <Button size="small" onClick={clearFilters}>
            Clear All
          </Button>
        </Box>
      )}

      {/* Main Content - Grid and Filters */}
      <Box sx={{ flex: 1, display: 'flex', gap: 2, overflow: 'hidden' }}>
        {/* Filter Panel (Desktop) */}
        {!isMobile && (
          <Box sx={{ width: 280, flexShrink: 0, overflow: 'auto' }}>{filterPanelContent}</Box>
        )}

        {/* Timetable Grid */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {filteredLessons.length > 0 ? (
            <TimetableGrid
              lessons={filteredLessons}
              periods={availableFilters.periods}
              days={displayDays}
              conflicts={relevantConflicts}
              onLessonClick={handleLessonClick}
              showTeacher={true}
              showRoom={true}
              showStudentGroup={true}
              colorBySubject={viewConfig.colorBy === 'subject'}
              highlightConflicts={viewConfig.showConflicts}
            />
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <CalendarTodayIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
              <Typography variant="h6" color="text.secondary">
                No lessons found
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Try adjusting your filters or selecting a different view mode
              </Typography>
              <Button variant="outlined" onClick={clearFilters}>
                Clear Filters
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Mobile Filter Drawer */}
      <Drawer anchor="right" open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)}>
        <Box sx={{ width: 300, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          {filterPanelContent}
        </Box>
      </Drawer>

      {/* Selected Lesson Detail (could be a dialog in future) */}
      {selectedLesson && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold">
              Selected Lesson: {selectedLesson.subjectId}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Teacher: {selectedLesson.teacherId} | Room: {selectedLesson.roomId} | Group:{' '}
              {selectedLesson.studentGroupId}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ViewTimetable;

